// libs
import React, { memo, useCallback, useEffect, useState, useMemo } from 'react';

// components
import IOElement from 'shared/components/IOElement';
import Placeholder from './Placeholder';
import WishlistProduct from 'shared/layouts/Wishlist/Mobile/components/Products/components/WishlistProduct';

// constants
import { BREAKPOINTS, DWEB_MAX_WIDTH } from 'shared/styles/base';
import { CLASSNAMES } from 'shared/layouts/Listing/constants';
import {
  DESKTOP_PRODUCT_WIDTH,
  GUTTER_SPACE_MOBILE,
  MAX_PRODUCTS_PER_ROW_MOBILE,
} from './constant';
import { SIZE_OF_WISHLIST_TO_BE_FETCHED } from 'shared/layouts/Wishlist/Mobile/constants';

// styles
import { Container, PageIO, ProductsContainer } from './styles';

// contexts
import { useLoaderContext } from 'shared/components/LoaderProvider/context';
import { useWishlistContext } from 'shared/layouts/Wishlist/API/context';
import { useResponsiveContext } from 'shared/components/ResponsiveLoader/ResponsiveContext';

// helpers
import { removeDuplicates } from './helpers';

// defs
interface IProducts {
  products: Listing.IProduct[];
  totalProducts: number;
  setShowPriceDropStrip: (val: boolean) => void;
}

function Products({ products, totalProducts, setShowPriceDropStrip }: IProducts) {
  const [productWidth, setProductWidth] = useState<number>(0);
  const [showIO, setShowIO] = useState(false);
  const { incrementPage, isFetching, isRemoving } = useWishlistContext();

  const { deviceWidth } = useResponsiveContext();

  const { showLoader, hideLoader } = useLoaderContext();

  // dweb support
  const isDesktop: boolean = deviceWidth >= BREAKPOINTS.DESKTOP;

  const filteredProducts = useMemo(() => removeDuplicates(products), [products]);

  useEffect(() => {
    return () => {
      hideLoader();
    };
  }, [hideLoader]);

  useEffect(() => {
    const width = Math.min(DWEB_MAX_WIDTH, deviceWidth);
    let newProductWidth = width / MAX_PRODUCTS_PER_ROW_MOBILE - GUTTER_SPACE_MOBILE;

    // dweb support
    if (isDesktop) {
      newProductWidth = DESKTOP_PRODUCT_WIDTH;
    }

    if (productWidth === newProductWidth) {
      return;
    }

    setProductWidth(newProductWidth);
  }, [deviceWidth, isDesktop, productWidth, setProductWidth]);

  useEffect(() => {
    if (isFetching || isRemoving) return;
    // if all the products are fetched (including the case where there is no
    // product in the wishlist) IOElement will be hidden
    if (totalProducts === filteredProducts.length) {
      setShowIO(false);
      return;
    }

    setShowIO(true);
  }, [filteredProducts.length, isFetching, isRemoving, totalProducts]);

  useEffect(() => {
    const isPriceDropFlag = filteredProducts.some(
      (product) => product.priceDropFlag && !product.priceRevealDTO && !product.isOutOfStock
    );
    setShowPriceDropStrip(isPriceDropFlag);
  }, [filteredProducts, setShowPriceDropStrip]);

  const onPageIoVisible = useCallback(() => {
    if (isFetching || isRemoving) return;

    incrementPage();

    setShowIO(false);
  }, [incrementPage, isFetching, isRemoving]);

  useEffect(() => {
    if (totalProducts === 0 && isFetching) showLoader();

    if (!isFetching) {
      hideLoader();
    }
  }, [hideLoader, isFetching, showLoader, totalProducts]);

  return (
    <Container>
      {totalProducts > 0 && (
        <ProductsContainer productWidth={productWidth}>
          {filteredProducts.length > 0 &&
            filteredProducts.map((product, i) => (
              <WishlistProduct
                key={i}
                keyParam={i}
                item={product}
                productWidth={productWidth}
                openInTab={isDesktop}
              />
            ))}

          {isFetching &&
            Array(Math.min(SIZE_OF_WISHLIST_TO_BE_FETCHED, totalProducts - filteredProducts.length))
              .fill(1)
              .map((_, idx) => <Placeholder className={CLASSNAMES.PRODUCT} key={idx} />)}
        </ProductsContainer>
      )}

      {showIO && !isFetching && (
        <IOElement onVisible={onPageIoVisible}>
          <PageIO />
        </IOElement>
      )}
    </Container>
  );
}

export default memo(Products);
